import { Messages } from 'config';
import { CashCardType } from 'types';
import { mapQuery } from 'utils/query';
import { Flex } from 'components/flex';
import { List } from 'components/list';
import { formatDate } from 'utils/date';
import { formatPrice } from 'utils/helpers';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { useDeleteMutation } from 'store/api/payout';
import { useCashManagerQuery } from 'store/api/event';
import { DeleteDialog } from 'components/delete-dialog';
import { DownloadButton } from 'components/specified-buttons';
import { CashManagerExpenseAndPayout } from 'store/api/event/types';
import { CreateBonusAdvanceDialog } from 'components/create-bonus-advance-dialog';
import {
  isMobile,
  useFilter,
  handleMutation,
  useSearchQuery,
  useDownloadFile,
} from 'hooks';

import { CashCard, CashCards } from '../cash-cards';

import { CashFlowProps } from './types';
import { paths, titles, template, footerTabletTemplate } from './config';

export const CashFlow = ({ event }: CashFlowProps) => {
  const mobile = isMobile();
  const { downloadFile } = useDownloadFile();
  const { debounceQuery, ...search } = useSearchQuery();

  const { data, ...props } = useCashManagerQuery(String(event.id), {
    skip: !event.id,
  });

  const [remove, mutation] = useDeleteMutation();
  handleMutation({
    ...mutation,
    successMessage: `Expense ${Messages.DELETED_SUCCESSFULLY}`,
  });

  const filteredValues = useFilter<CashManagerExpenseAndPayout>({
    paths,
    query: debounceQuery,
    data: data?.payoutsAndExpenses || [],
  });

  return (
    <Flex gap={16} flexDirection={mobile ? 'column' : 'row'}>
      <CashCards
        data={data}
        eventId={event.id}
        customTotalCash={data?.totalCollected}
      >
        {!!data?.refundAmount && (
          <CashCard
            title="Refund"
            type={CashCardType.Out}
            amount={data?.refundAmount}
          />
        )}
      </CashCards>
      <List
        {...mapQuery(props)}
        {...search}
        count={filteredValues.length}
        mainWrapperStyles={{ padding: 0 }}
        searchContainerStyle={{ margin: '0 0 16px 0' }}
        header={
          <ListHeader padding="8px" titles={titles} template={template} />
        }
        searchBarRightSlot={
          <CreateBonusAdvanceDialog
            withReceipt
            isWideDialog
            eventId={event.id}
            isOnlyCashLocation
            showSelectExpensesType
            customTitle="Create cashflow"
          />
        }
        footer={
          <ListCard
            selected
            padding="16px"
            template={template}
            tabletTemplate={footerTabletTemplate}
            mobileTemplate={footerTabletTemplate}
            values={['Total', formatPrice(data?.totalOut)]}
          />
        }
      >
        {filteredValues.map((value) => (
          <ListCard
            key={value.id}
            headers={titles}
            template={template}
            values={[
              formatDate(value?.date),
              formatPrice(value?.amount),
              value?.type,
              value?.employee,
              value?.notes,
              value?.category,
              <Flex gap={16} justifyContent="flex-end">
                {value.receiptUrl && (
                  <DownloadButton
                    url={value.receiptUrl}
                    onDownloadFile={downloadFile}
                  />
                )}

                {value?.type !== 'Expense' && (
                  <DeleteDialog
                    id={value?.id}
                    action={remove}
                    title={`Delete ${value.type}`}
                    name={`${formatPrice(value?.amount)}`}
                  />
                )}
              </Flex>,
            ]}
          />
        ))}
      </List>
    </Flex>
  );
};
